import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, Moon, Sun, Workflow, AlertCircle, Wallet, Clock, PauseCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { getTz } from "@/lib/tz";
import { NotificationsDropdown } from "@/features/notifications/NotificationsDropdown";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
// (eliminamos imports duplicados — useAuthStore solo se usa para perms aquí)
import { logout } from "@/lib/api";
import { useJobCounts } from "@/features/jobs/api";
import { PagoRapidoDialog } from "@/features/pago-rapido/PagoRapidoDialog";
import { useDarkMode } from "@/lib/theme";

const ROUTE_LABELS: Record<string, string> = {
  "": "Dashboard",
  clientes: "Clientes",
  planes: "Planes",
  redes: "Redes",
  routers: "Routers",
  contratos: "Contratos",
  facturas: "Facturas",
  pagos: "Pagos",
  cobranza: "Cobranza",
  usuarios: "Usuarios del sistema",
  ajustes: "Ajustes",
  // rc136 — faltaban (en móvil el título es lo único que dice dónde estás)
  tickets: "Tickets",
  prospectos: "Prospectos",
  sitios: "Sitios",
  mapa: "Mapa",
  equipos: "Equipos",
  vpn: "VPN",
  promesas: "Promesas",
  analisis: "Análisis",
  auditoria: "Auditoría",
  importacion: "Importación",
  jobs: "Jobs MikroTik",
  seguridad: "Mi cuenta",
  empresas: "Empresas",
};

// rc136 — useDarkMode vive en lib/theme.ts (compartido con el pie del menú móvil).

export function Topbar({ onMenu }: { onMenu?: () => void } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { isDark, toggle } = useDarkMode();
  const permissions = useAuthStore((s) => s.permissions);
  const canPagar = permissions.includes("pagos.write");
  const canVerJobs = permissions.includes("routers.read");
  // Solo cargamos counts si puede ver jobs (evita 403 silencioso)
  const { data: jobCounts } = useJobCounts({ enabled: canVerJobs });
  const activeJobs = (jobCounts?.queued ?? 0) + (jobCounts?.running ?? 0);
  const errorJobs = jobCounts?.error ?? 0;
  const [pagoRapidoOpen, setPagoRapidoOpen] = useState(false);

  // Atajo de teclado: Ctrl/Cmd + K abre Pago Rápido
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPagoRapidoOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const parts = location.pathname.split("/").filter(Boolean);
  const current = ROUTE_LABELS[parts[0] ?? ""] ?? parts.join(" / ");
  // rc136 — en móvil el indicador de jobs solo aparece si hay algo que ver
  const jobsVisibleMovil = activeJobs > 0 || errorJobs > 0;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    // rc92 — paridad NMS: h-[52px] + bg-background (mismo color que la página,
    // ya no bg-card) + hairline border-border + paddings px-3 md:px-5.
    // rc135.1 — PWA iOS (status bar translúcida): la barra de estado del
    // teléfono se dibuja ENCIMA del contenido; sin este padding el reloj
    // tapaba el botón del menú. env(safe-area-inset-top) = 0 en navegador normal.
    <header className="relative z-30 flex min-h-[52px] items-center justify-between border-b border-border glass-bar px-3 md:rounded-[14px] md:border md:shadow-[inset_0_1px_0_hsl(0_0%_100%/0.05),0_10px_30px_rgb(0_0_0/0.18)] pt-[env(safe-area-inset-top)] md:px-5">
      {/* Breadcrumbs / title */}
      <div className="flex items-center gap-2">
        {onMenu && (
          <button
            onClick={onMenu}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground md:hidden"
            aria-label="Abrir menú"
          >
            <Menu size={15} />
          </button>
        )}
        {/* rc146.3 — escritorio: el módulo ya se ve en la barra lateral; aquí va
            la fecha y hora del ISP (zona de Ajustes). El título queda para lectores
            de pantalla. En celular se sigue mostrando el nombre. */}
        <h1 className="truncate text-[12px] font-medium text-foreground max-md:max-w-[38vw] md:sr-only">{current}</h1>
        <RelojIsp />
        <AvisoPausa />
      </div>

      {/* Right side: pago rápido + theme + user */}
      <div className="flex items-center gap-1.5">
        {/* F84: botón Pago Rápido global (Ctrl/Cmd + K). Solo si puede aplicar pagos. */}
        {canPagar && (
          <Button
            size="sm"
            variant="default"
            onClick={() => setPagoRapidoOpen(true)}
            className="gap-1.5"
            title="Aplicar pago rápido (Ctrl+K)"
          >
            <Wallet size={14} />
            <span className="hidden sm:inline">Pago rápido</span>
            <kbd className="hidden md:inline-flex h-4 items-center rounded border border-primary-foreground/30 bg-primary-foreground/10 px-1 text-[9px] font-medium">
              ⌘K
            </kbd>
          </Button>
        )}
        {/* F16: indicador de jobs MikroTik (solo admin/técnico) */}
        {canVerJobs && (
          <Link
            to="/jobs"
            className={cn(
              "relative inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground",
              !jobsVisibleMovil && "max-md:hidden",
            )}
            title="Jobs MikroTik"
          >
            <Workflow size={14} />
            {activeJobs > 0 && (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                {activeJobs}
              </span>
            )}
            {errorJobs > 0 && (
              <span
                className="inline-flex h-4 items-center gap-0.5 rounded-full bg-destructive/15 px-1.5 text-[10px] font-semibold text-destructive"
                title={`${errorJobs} job(s) en error`}
              >
                <AlertCircle size={10} /> {errorJobs}
              </span>
            )}
          </Link>
        )}
        {/* rc32: cajeros NO ven campanita de notificaciones — el feed es
            global (pagos/promesas/jobs) y no aplica a su flujo. Sus push
            notifications también están bloqueadas (ver notify_payment_received). */}
        {user?.rol?.nombre !== "cajero" && <NotificationsDropdown />}
        {/* rc136 — tema y salir se van al pie del menú en móvil (menos iconos arriba) */}
        <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme" className="max-md:hidden">
          {isDark ? <Sun size={15} /> : <Moon size={15} />}
        </Button>

        {/* rc92 — avatar/usuario con las medidas de NMS (h-7 w-7 + 11px) */}
        <Link
          to="/seguridad"
          className="ml-2 flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent max-md:ml-0 max-md:px-1"
          title="Mi cuenta y seguridad"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-secondary text-[11px] font-semibold text-muted-foreground">
            {(user?.email ?? "?")[0]?.toUpperCase()}
          </div>
          <div className="hidden text-[11px] leading-tight sm:block">
            <div className="font-medium text-foreground">{user?.email ?? "—"}</div>
            <div className="text-muted-foreground">
              {user?.rol?.nombre ?? "—"}
            </div>
          </div>
        </Link>
        <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Cerrar sesión" className="max-md:hidden">
          <LogOut size={15} />
        </Button>
      </div>

      {/* F84: dialog global de Pago Rápido */}
      <PagoRapidoDialog open={pagoRapidoOpen} onOpenChange={setPagoRapidoOpen} />
    </header>
  );
}

/** rc146.3 — fecha y hora en la zona del ISP (sistema.timezone), al minuto. */
function RelojIsp() {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setAhora(new Date()), 1_000);
    return () => window.clearInterval(t);
  }, []);
  const tz = getTz();
  const parte = (o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("es-MX", { timeZone: tz, ...o }).format(ahora).replace(/\./g, "");
  const dia = parte({ weekday: "short" });
  const fecha = `${dia.charAt(0).toUpperCase()}${dia.slice(1)} ${parte({ day: "numeric" })} ${parte({ month: "short" })}`;
  const hora = new Intl.DateTimeFormat("es-MX", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(ahora);
  return (
    <div className="flex items-center gap-2 text-[12px] max-md:hidden" aria-label={`${fecha} ${hora}`}>
      <Clock size={14} className="text-electric" />
      <span className="text-muted-foreground">{fecha}</span>
      <span className="font-mono font-medium tabular-nums text-foreground">{hora}</span>
    </div>
  );
}

/** rc147 — aviso visible mientras la facturación o la cobranza automáticas
 *  estén en pausa (Ajustes › Facturación). Lo ve todo el equipo. */
function AvisoPausa() {
  const { data } = useQuery<{ facturacion_pausada: boolean; cobranza_pausada: boolean }>({
    queryKey: ["automatismos"],
    queryFn: () => apiCall("get", "/api/v1/automatismos"),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 0,
  });
  if (!data || (!data.facturacion_pausada && !data.cobranza_pausada)) return null;
  const que = data.facturacion_pausada && data.cobranza_pausada
    ? "Facturación y cobranza en pausa"
    : data.facturacion_pausada ? "Facturación en pausa" : "Cobranza en pausa";
  return (
    <Link
      to="/ajustes/facturacion"
      title="Automatismos en pausa — Ajustes › Facturación y cobranza"
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-warn/40 bg-warn/10 px-2 py-0.5 text-[11px] font-medium text-warn hover:bg-warn/15"
    >
      <PauseCircle size={12} />
      <span className="max-md:hidden">{que}</span>
      <span className="md:hidden">En pausa</span>
    </Link>
  );
}
